// src/domains/core/context-intelligence/adapters/productivityAdapter.ts

import type {
  IndicatorValue,
  IndicatorDataQuery,
  IndicatorDataResponse,
} from "../types/indicators.types";

import { useSupabaseTasks } from "@/domains/productivity/timetracq/hooks/data/useSupabasetasks";

import { indicatorRegistry } from "../registry/indicatorRegistry";

/**
 * Productivity Data Adapter
 * Adapts Productivity domain data (TimeTracQ) to unified Indicator format
 */
export const ProductivityAdapter = {
  /**
   * Get indicator data for a specific indicator
   *
   * @param indicatorId
   *
   * @param query
   *
   * @returns {Promise<IndicatorDataResponse | null>} Indicator data or null
   *
   */
  async getIndicatorData(
    indicatorId: string,
    query: IndicatorDataQuery,
  ): Promise<IndicatorDataResponse | null> {
    const indicator = indicatorRegistry.get(indicatorId);
    if (!indicator) {
      return null;
    }

    if (indicator.source === "timetracq") {
      return ProductivityAdapter.getTimeTracQData(indicatorId, query);
    }

    return null;
  },

  /**
   * Get TimeTracQ indicator data
   * Note: This is a simplified version - in production, this would need
   * to access historical data from Supabase
   *
   * @param indicatorId
   *
   * @param query
   *
   * @returns {Promise<IndicatorDataResponse | null>} TimeTracQ indicator data or null
   *
   */
  getTimeTracQData(
    indicatorId: string,
    query: IndicatorDataQuery,
  ): Promise<IndicatorDataResponse | null> {
    const indicator = indicatorRegistry.get(indicatorId);
    if (!indicator) {
      return Promise.resolve(null);
    }

    // Placeholder - actual implementation would:
    // 1. Query Supabase for historical task data
    // 2. Calculate metrics (completion rate, open tasks, etc.)
    // 3. Filter by date range
    // 4. Map to IndicatorValue format
    // 5. Apply aggregation if requested

    const values: IndicatorValue[] = [];

    return Promise.resolve({
      indicator,
      metadata: {
        count: values.length,
        dateRange: { end: query.endDate, start: query.startDate },
        source: "timetracq",
      },
      values,
    });
  },

  /**
   * Calculate task completion rate from tasks array
   *
   * @param tasks
   *
   * @returns {number} Task completion rate as percentage
   *
   */
  calculateTaskCompletionRate(tasks: { status: null | string }[]): number {
    if (tasks.length === 0) {
      return 0;
    }

    const completed = tasks.filter(
      (task) => task.status === "done" || task.status === "completed",
    ).length;
    return (completed / tasks.length) * 100;
  },

  /**
   * Count open tasks
   *
   * @param tasks
   *
   * @returns {number} Number of open tasks
   *
   */
  countOpenTasks(tasks: { status: null | string }[]): number {
    return tasks.filter(
      (task) => task.status === "open" || task.status === "in_progress" || task.status === null,
    ).length;
  },
};

/**
 * React Hook for Productivity Adapter
 * Provides reactive access to productivity indicator data
 *
 * @param indicatorId
 *
 * @returns {object} Productivity indicator data with value, loading, error, and indicator
 *
 */
export function useProductivityIndicatorData(indicatorId: string) {
  const indicator = indicatorRegistry.get(indicatorId);
  const { loading: tasksLoading, tasks } = useSupabaseTasks();

  let currentValue: null | number = null;
  const loading = tasksLoading;
  const error: Error | null = null;

  if (indicator?.source === "timetracq") {
    switch (indicatorId) {
      case "timetracq_task_completion_rate": {
        currentValue = ProductivityAdapter.calculateTaskCompletionRate(tasks);
        break;
      }
      case "timetracq_open_tasks": {
        currentValue = ProductivityAdapter.countOpenTasks(tasks);
        break;
      }
      case "timetracq_total_tasks": {
        currentValue = tasks.length;
        break;
      }
    }
  }

  return {
    error,
    indicator,
    loading,
    value: currentValue,
  };
}
